import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const SteelReceiptReport = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    srr_code: '',
    srr_no: '',
    srr_date: '',
    mill_code: '',
    steel_code: '',
    heat_no: '',
    received_bars: '',
    received_quantity: '',
    accepted_bars: '',
    accepted_quantity: '',
    rejected_bars: '',
    rejected_quantity: '',
    remarks: '',
    location: '',
    status: 'A'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [steels, setSteels] = useState([]);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
    fetchSuppliers();
    fetchSteels();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/raw-material/srr');
      // Fix: Handle different possible response structures
      const srrData = response.data?.data?.srr || response.data?.srr || response.data?.data || response.data || [];
      setRecords(Array.isArray(srrData) ? srrData : []);
    } catch (error) {
      console.error('Error fetching SRR records:', error);
      setRecords([]); // Ensure records is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/masters/suppliers');
      setSuppliers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchSteels = async () => {
    try {
      const response = await api.get('/masters/steels');
      setSteels(response.data.data || []);
    } catch (error) {
      console.error('Error fetching steels:', error);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      srr_code: '',
      srr_no: '',
      srr_date: new Date().toISOString().split('T')[0],
      mill_code: '',
      steel_code: '',
      heat_no: '',
      received_bars: '',
      received_quantity: '',
      accepted_bars: '',
      accepted_quantity: '',
      rejected_bars: '',
      rejected_quantity: '',
      remarks: '',
      location: '',
      status: 'A'
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      // Convert empty strings to null for numeric fields
      const data = { ...currentRecord };
      const numericFields = ['received_bars', 'received_quantity', 'accepted_bars', 'accepted_quantity', 'rejected_bars', 'rejected_quantity'];
      numericFields.forEach(field => {
        if (data[field] === '' || data[field] === undefined) {
          data[field] = null;
        }
      });

      if (editMode) {
        await api.put(`/raw-material/srr/${currentRecord.srr_id}`, data);
      } else {
        await api.post('/raw-material/srr', data);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving SRR record:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'A': return 'success';
      case 'R': return 'error';
      case 'P': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'A': return 'Accepted';
      case 'R': return 'Rejected';
      case 'P': return 'Pending';
      default: return status;
    }
  };

  const filteredRecords = (Array.isArray(records) ? records : []).filter(record =>
    record.srr_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.heat_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.mill_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom color="primary">
          Steel Receipt Report (SRR)
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Incoming steel receipt management and tracking
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Records
              </Typography>
              <Typography variant="h4">
                {Array.isArray(records) ? records.length : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Accepted
              </Typography>
              <Typography variant="h4" color="success.main">
                {(Array.isArray(records) ? records : []).filter(r => r.status === 'A' || r.status === 'ACCEPTED').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {(Array.isArray(records) ? records : []).filter(r => r.status === 'P' || r.status === 'U/TESTIN').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Rejected
              </Typography>
              <Typography variant="h4" color="error.main">
                {(Array.isArray(records) ? records : []).filter(r => r.status === 'R' || r.status === 'REJECTED').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search SRR..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
        >
          Export
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>SRR No.</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Mill Code</strong></TableCell>
                <TableCell><strong>Steel Code</strong></TableCell>
                <TableCell><strong>Heat No.</strong></TableCell>
                <TableCell><strong>Received Qty</strong></TableCell>
                <TableCell><strong>Accepted Qty</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Alert severity="info">
                      No SRR records found. This data will be available once transaction data is migrated.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((record) => (
                  <TableRow key={record.srr_code} hover>
                    <TableCell>{record.srr_no}</TableCell>
                    <TableCell>{record.srr_date}</TableCell>
                    <TableCell>{record.mill_code}</TableCell>
                    <TableCell>{record.steel_code}</TableCell>
                    <TableCell>{record.heat_no}</TableCell>
                    <TableCell>{record.received_quantity}</TableCell>
                    <TableCell>{record.accepted_quantity}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(record.status)}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(record)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Steel Receipt Report' : 'Add New Steel Receipt Report'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SRR Number"
                value={currentRecord.srr_no}
                onChange={(e) => setCurrentRecord({...currentRecord, srr_no: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={currentRecord.srr_date}
                onChange={(e) => setCurrentRecord({...currentRecord, srr_date: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Mill Code</InputLabel>
                <Select
                  value={currentRecord.mill_code}
                  label="Mill Code"
                  onChange={(e) => setCurrentRecord({...currentRecord, mill_code: e.target.value})}
                >
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.supplier_code} value={supplier.supplier_code}>
                      {supplier.supplier_code} - {supplier.supplier_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Steel Code"
                value={currentRecord.steel_code}
                onChange={(e) => setCurrentRecord({...currentRecord, steel_code: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heat Number"
                value={currentRecord.heat_no}
                onChange={(e) => setCurrentRecord({...currentRecord, heat_no: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Received Quantity"
                type="number"
                value={currentRecord.received_quantity}
                onChange={(e) => setCurrentRecord({...currentRecord, received_quantity: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Accepted Quantity"
                type="number"
                value={currentRecord.accepted_quantity}
                onChange={(e) => setCurrentRecord({...currentRecord, accepted_quantity: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={currentRecord.status}
                  label="Status"
                  onChange={(e) => setCurrentRecord({...currentRecord, status: e.target.value})}
                >
                  <MenuItem value="A">Accepted</MenuItem>
                  <MenuItem value="P">Pending</MenuItem>
                  <MenuItem value="R">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={currentRecord.remarks}
                onChange={(e) => setCurrentRecord({...currentRecord, remarks: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SteelReceiptReport;